import { Loader } from "lucide-react";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { useState } from "react";
import { PricesData } from "../../../../../lib/languages";
import { useLanguage } from "../../../../../context/LanguageContext";
import { parseField } from "./../../../../../lib/utils";

export default function AddPriceDialog({
  setFormData,
  categories,
  subCategories,
  isAddDialogOpen,
  setIsAddDialogOpen,
  formData,
  handleInputChange,
  handleSelectChange,
  resetForm,
  handleCreatePrice,
  handleTimeChange,
  btnLoading,
  currencies,
  errors,
}) {
  const { language } = useLanguage();
  const strings = PricesData[language] || PricesData["en"];

  return (
    <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
      <DialogContent
        className="sm:max-w-[500px] max-h-[90vh] overflow-auto"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle>{strings.addPriceTitle}</DialogTitle>
          <DialogDescription>{strings.addPriceDesc}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="name" className="text-start text-sm font-medium">
              {strings.nameLabel}
            </label>
            <div className="col-span-3">
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={strings.namePlaceholder}
              />
              {errors?.name && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.name[0]}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="price" className="text-start text-sm font-medium">
              {strings.priceLabel}
            </label>
            <div className="col-span-3">
              <Input
                id="price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
              {errors?.price && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.price[0]}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label
              htmlFor="currency_id"
              className="text-start text-sm font-medium"
            >
              {strings.currencyLabel}
            </label>
            <div className="col-span-3">
              <Select
                value={formData.currency_id.toString()}
                onValueChange={(value) =>
                  handleSelectChange("currency_id", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={strings.selectCurrency} />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((currency) => (
                    <SelectItem
                      key={currency.id}
                      value={currency.id.toString()}
                    >
                      {currency.code} ({currency.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors?.currency_id && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.currency_id[0]}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label
              htmlFor="discount"
              className="text-start text-sm font-medium"
            >
              {strings.discountLabel}
            </label>
            <div className="col-span-3">
              <Input
                id="discount"
                name="discount"
                type="number"
                value={formData.discount}
                onChange={handleInputChange}
                min="0"
                max="100"
              />
              {errors?.discount && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.discount[0]}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label
              htmlFor="duration"
              className="text-start text-sm font-medium"
            >
              {strings.durationLabelForm}
            </label>
            <div className="col-span-3">
              <TimePicker
                format="HH:mm"
                value={dayjs(`2023-01-01T${formData.duration}`)}
                onChange={handleTimeChange}
                className="w-full"
                showNow={false}
                allowClear={false}
              />
              {errors?.duration && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.duration[0]}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label
              htmlFor="lesson_num"
              className="text-start text-sm font-medium"
            >
              {strings.lessonNumLabel}
            </label>
            <div className="col-span-3">
              <Input
                id="lesson_num"
                name="lesson_num"
                type="number"
                value={formData.lesson_num}
                onChange={handleInputChange}
                min="0"
              />
              {errors?.lesson_num && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.lesson_num[0]}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="type" className="text-start text-sm font-medium">
              {strings.categoriesLabel}
            </label>
            <div className="col-span-3">
              <Select
                value={+formData.category_id || ""}
                onValueChange={(value) => {
                  handleSelectChange("category_id", +value);
                  setFormData((prev) => ({
                    ...prev,
                    sub_category_id: null,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={strings.selectCategoryPlaceholder}
                  />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {parseField(category.title, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors?.category && (
                <p className="text-red-500 font-semibold text-sm">
                  {strings.categoryRequiredError}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="type" className="text-start text-sm font-medium">
              {strings.subCategoriesLabel}
            </label>
            <div className="col-span-3">
              <Select
                value={+formData.sub_category_id || ""}
                onValueChange={(value) => {
                  handleSelectChange("sub_category_id", +value);

                  setFormData((prev) => ({
                    ...prev,
                    category_id: null,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={strings.selectSubCategoryPlaceholder}
                  />
                </SelectTrigger>
                <SelectContent>
                  {subCategories.map((subCategory) => (
                    <SelectItem key={subCategory.id} value={subCategory.id}>
                      {parseField(subCategory.title, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors?.subCategories && (
                <p className="text-red-500 font-semibold text-sm">
                  {strings.subCategoryRequiredError}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="type" className="text-start text-sm font-medium">
              {strings.typeLabel}
            </label>
            <div className="col-span-3">
              <Select
                value={formData.type}
                onValueChange={(value) => handleSelectChange("type", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={strings.selectType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">
                    {strings.optionIndividual}
                  </SelectItem>
                  <SelectItem value="group">{strings.optionGroup}</SelectItem>
                </SelectContent>
              </Select>
              {errors?.type && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.type[0]}
                </p>
              )}
            </div>
          </div>

          {formData.type === "group" && (
            <div className="grid grid-cols-4 items-center gap-4">
              <label
                htmlFor="group_num"
                className="text-start text-sm font-medium"
              >
                {strings.groupNumLabel}
              </label>
              <div className="col-span-3">
                <Input
                  id="group_num"
                  name="group_num"
                  type="number"
                  value={formData.group_num}
                  onChange={handleInputChange}
                  min="0"
                />
                {errors?.group_num && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.group_num[0]}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsAddDialogOpen(false);
              resetForm();
            }}
          >
            {strings.cancel}
          </Button>
          <Button onClick={handleCreatePrice} disabled={btnLoading.create}>
            {btnLoading.create ? (
              <Loader className="animate-spin h-4 w-4" />
            ) : (
              strings.create
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
